/**
 * Welke pagina's onder *Instellingen* vallen, per thema, en met welk recht je ze ziet.
 *
 * De startpagina van Instellingen toont deze pagina's als tegels; de shell gebruikt dezelfde lijst om
 * *Instellingen* in het menu op te laten lichten en het kruimelpad te tonen zodra je op een van deze pagina's
 * zit. Eén lijst voor alle drie, zodat een pagina die erbij komt niet op één plek vergeten wordt.
 *
 * ⚠️ DIT IS EEN EXPERIMENT (feedback 22-10, Nick) en geen vastgestelde indeling. De indeling en de labels van het
 * menu liggen bij PO/PM. Net als bij `adminNav` is verschuiven daarom één regel wijzigen.
 *
 * Twee soorten pagina's staan hier naast elkaar, en dat is opzet:
 *
 *  - de onderdelen die vroeger een tabblad op `/settings` waren, nu met een eigen adres onder `/settings/…`;
 *  - losse beheerpagina's die hun eigen adres houden (`/themes`, `/workflows`, …), zodat een bestaande link
 *    blijft werken. Die stonden eerder in een eigen menugroep.
 *
 * Het recht per pagina is hetzelfde als dat van zijn route-guard in `router/routes.tsx`. Loopt dat uit de pas,
 * dan toont de startpagina een tegel die naar een doorverwijzing leidt (of verbergt hij er een die wél open kan).
 */
import type { ReactNode } from 'react';
import {
  Bell,
  BookOpen,
  CalendarX,
  Certificate,
  CheckSquare,
  ClockCounterClockwise,
  FlowArrow,
  GitBranch,
  GraduationCap,
  Notepad,
  PlugsConnected,
  Prohibit,
  Stamp,
  Tag,
  TextT,
  Translate,
} from '@phosphor-icons/react';

/** De thema's van de startpagina, in rendervolgorde. Het id is Engels en taal-onafhankelijk. */
export type SettingsThemeId = 'catalog' | 'processes' | 'textsMessages' | 'evaluationData' | 'integrations';

export const SETTINGS_THEMES: ReadonlyArray<{ id: SettingsThemeId; labelKey: string }> = [
  { id: 'catalog', labelKey: 'settingsHub.themes.catalog' },
  { id: 'processes', labelKey: 'settingsHub.themes.processes' },
  { id: 'textsMessages', labelKey: 'settingsHub.themes.textsMessages' },
  { id: 'evaluationData', labelKey: 'settingsHub.themes.evaluationData' },
  { id: 'integrations', labelKey: 'settingsHub.themes.integrations' },
];

/** De onderdelen met een eigen adres onder `/settings/…` — vroeger de tabbladen van de hub. */
export type SettingsModuleId = 'catalog' | 'catalogs' | 'cancellation' | 'approvals' | 'messages' | 'languages' | 'questionnaires' | 'documents' | 'retention';

export const SETTINGS_MODULES: readonly SettingsModuleId[] = ['catalog', 'catalogs', 'cancellation', 'approvals', 'messages', 'languages', 'questionnaires', 'documents', 'retention'];

/** Het recht van elk onderdeel onder `/settings/…`: lezen. Opslaan vraagt daarbinnen `tenant_config.configure`. */
export const SETTINGS_MODULE_ATOM = 'tenant_config.view';

/**
 * De certificaten hebben hun eigen rechten (`document_template.*`, #578), los van de instellingen: wie sjablonen
 * beheert, hoeft de rest van de inrichting niet te mogen. Het paneel zelf beslist wat er te wijzigen is.
 */
export const DOCUMENTS_ATOM = 'document_template.view';

/** Het recht van de route van een onderdeel: hetzelfde als dat van zijn tegel hieronder. */
export function settingsModuleAtom(module: SettingsModuleId): string {
  return module === 'documents' ? DOCUMENTS_ATOM : SETTINGS_MODULE_ATOM;
}

export interface SettingsPage {
  /** Het adres. Een onderdeel onder `/settings/…`, of het eigen adres van een losse pagina. */
  path: string;
  theme: SettingsThemeId;
  labelKey: string;
  descriptionKey: string;
  icon: ReactNode;
  /** Hetzelfde atoom als de route-guard van de pagina. */
  permission: string;
}

const ICON = { size: 24, weight: 'regular' as const, 'aria-hidden': true };

export const SETTINGS_PAGES: readonly SettingsPage[] = [
  { path: '/settings/catalog', theme: 'catalog', labelKey: 'settingsHub.pages.catalog', descriptionKey: 'settingsHub.descriptions.catalog', icon: <GraduationCap {...ICON} />, permission: SETTINGS_MODULE_ATOM },
  { path: '/settings/catalogs', theme: 'catalog', labelKey: 'settingsHub.tabs.catalogs', descriptionKey: 'settingsHub.descriptions.catalogs', icon: <BookOpen {...ICON} />, permission: SETTINGS_MODULE_ATOM },
  { path: '/settings/cancellation', theme: 'catalog', labelKey: 'settingsHub.tabs.cancellation', descriptionKey: 'settingsHub.descriptions.cancellation', icon: <CalendarX {...ICON} />, permission: SETTINGS_MODULE_ATOM },
  { path: '/themes', theme: 'catalog', labelKey: 'nav.themes', descriptionKey: 'settingsHub.descriptions.themes', icon: <Tag {...ICON} />, permission: 'theme.manage' },
  { path: '/cancellation-reasons', theme: 'catalog', labelKey: 'nav.cancellationReasons', descriptionKey: 'settingsHub.descriptions.cancellationReasons', icon: <Prohibit {...ICON} />, permission: 'cancellation_reason.manage' },
  { path: '/settings/approvals', theme: 'processes', labelKey: 'settingsHub.tabs.approvals', descriptionKey: 'settingsHub.descriptions.approvals', icon: <CheckSquare {...ICON} />, permission: SETTINGS_MODULE_ATOM },
  { path: '/approval-flows', theme: 'processes', labelKey: 'nav.approvalFlows', descriptionKey: 'settingsHub.descriptions.approvalFlows', icon: <GitBranch {...ICON} />, permission: 'approval_flow.view' },
  { path: '/workflows', theme: 'processes', labelKey: 'nav.workflows', descriptionKey: 'settingsHub.descriptions.workflows', icon: <FlowArrow {...ICON} />, permission: 'workflow.view' },
  { path: '/texts', theme: 'textsMessages', labelKey: 'nav.localeOverrides', descriptionKey: 'settingsHub.descriptions.texts', icon: <TextT {...ICON} />, permission: 'text_label.manage' },
  { path: '/settings/messages', theme: 'textsMessages', labelKey: 'settingsHub.tabs.messages', descriptionKey: 'settingsHub.descriptions.messages', icon: <Bell {...ICON} />, permission: SETTINGS_MODULE_ATOM },
  // De contenttalen (punt 29): de klant kiest ze zelf. De interfacetalen staan in de superadmin-console.
  { path: '/settings/languages', theme: 'textsMessages', labelKey: 'settingsHub.tabs.languages', descriptionKey: 'settingsHub.descriptions.languages', icon: <Translate {...ICON} />, permission: SETTINGS_MODULE_ATOM },
  { path: '/settings/questionnaires', theme: 'evaluationData', labelKey: 'settingsHub.tabs.questionnaires', descriptionKey: 'settingsHub.descriptions.questionnaires', icon: <Notepad {...ICON} />, permission: SETTINGS_MODULE_ATOM },
  // De certificaten (#578): de sjablonen zelf, en de instellingen eromheen (nummering, BCS-sjablonen). Ze stonden
  // eerst als menu-item in de groep Instellingen; die groep is weg, dus een tegel, zoals Teksten en Workflows.
  { path: '/document-templates', theme: 'evaluationData', labelKey: 'nav.documentTemplates', descriptionKey: 'settingsHub.descriptions.documentTemplates', icon: <Certificate {...ICON} />, permission: DOCUMENTS_ATOM },
  { path: '/settings/documents', theme: 'evaluationData', labelKey: 'settingsHub.tabs.documents', descriptionKey: 'settingsHub.descriptions.documents', icon: <Stamp {...ICON} />, permission: DOCUMENTS_ATOM },
  { path: '/settings/retention', theme: 'evaluationData', labelKey: 'settingsHub.tabs.retention', descriptionKey: 'settingsHub.descriptions.retention', icon: <ClockCounterClockwise {...ICON} />, permission: SETTINGS_MODULE_ATOM },
  { path: '/lti-providers', theme: 'integrations', labelKey: 'nav.ltiProviders', descriptionKey: 'settingsHub.descriptions.lti', icon: <PlugsConnected {...ICON} />, permission: 'integration.manage' },
];

/**
 * Elk recht dat minstens één tegel laat zien. Dat zijn de rechten van de startpagina zelf en van het menu-item
 * *Instellingen*: wie alleen workflows mag beheren, moet er via Instellingen nog steeds bij kunnen, nu de losse
 * menugroep weg is.
 */
export const SETTINGS_ATOMS: readonly string[] = [...new Set(SETTINGS_PAGES.map((page) => page.permission))];

/**
 * De tegels die deze gebruiker ziet: met het recht van de pagina, en niet als een uitgezette module het adres
 * verbergt (de server stuurt die adressen in `hidden_menu_items`, net als voor het menu).
 */
export function visibleSettingsPages(held: readonly string[], hidden: readonly string[] = []): SettingsPage[] {
  return SETTINGS_PAGES.filter((page) => held.includes(page.permission) && !hidden.includes(page.path));
}

/**
 * De instellingen-pagina waar dit adres onder valt, of `null`. Ook een dieper adres telt mee
 * (`/workflows/12` valt onder Workflows); `/settings` zelf is de startpagina en geen tegel.
 */
export function settingsPageFor(pathname: string): SettingsPage | null {
  const matches = SETTINGS_PAGES.filter((page) => pathname === page.path || pathname.startsWith(`${page.path}/`));
  return matches.sort((a, b) => b.path.length - a.path.length)[0] ?? null;
}
